import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { Checkmark20Px, ProgressButton, Undo20Px } from "@qubit/autoparts";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { openWeightEntryModal } from "~/features/autostorePicking/autostorePicking.slice";

import { selectBinIsPresent } from "~/redux/selectors/autostoreSelectors";

const NUMPAD_ROWS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"]
] as const;

function formatWeight(hundredths: number): string {
  return (hundredths / 100).toFixed(2);
}

function appendDigit(current: number, digit: number): number {
  if (String(current).length < 4) {
    return current * 10 + digit;
  }
  return current;
}

function backspace(current: number): number {
  return Math.floor(current / 10);
}

function NumpadKey({
  label,
  onClick,
  borderLeft
}: {
  label: string;
  onClick: () => void;
  borderLeft: boolean;
}) {
  const { palette } = useTheme();
  return (
    <Button
      aria-label={label}
      onClick={onClick}
      variant="text"
      color="secondary"
      sx={{
        width: 80,
        height: 80,
        borderRadius: 0,
        fontSize: 32,
        fontWeight: 500,
        lineHeight: 1,
        color: "text.primary",
        borderLeft: borderLeft ? `1px solid ${palette.divider}` : "none"
      }}
    >
      {label}
    </Button>
  );
}

function NumpadRow({ children }: { children: React.ReactNode[] }) {
  const { palette } = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      paddingY={1}
      sx={{
        borderTop: `1px solid ${palette.divider}`
      }}
    >
      {children}
    </Stack>
  );
}

type WeightEntryModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (weight: number) => Promise<boolean>;
};

export function WeightEntryModal({
  open,
  onClose,
  onConfirm
}: WeightEntryModalProps) {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const [weightHundredths, setWeightHundredths] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDialogClose = () => {
    if (isSubmitting) return;
    setWeightHundredths(0);
    onClose();
  };

  const handleConfirm = async () => {
    if (weightHundredths <= 0 || isSubmitting) return;

    const weight = weightHundredths / 100;
    setIsSubmitting(true);
    try {
      const success = await onConfirm(weight);
      if (success) {
        setWeightHundredths(0);
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDigitPress = (digit: number) => {
    setWeightHundredths((current) => appendDigit(current, digit));
  };

  const handleBackspace = () => {
    setWeightHundredths((current) => backspace(current));
  };

  const handleKeyDown = (key: string) => {
    if (key === "Backspace") {
      handleBackspace();
    } else if (key.match(/^\d$/)) {
      handleDigitPress(Number(key));
    }
  };

  const isConfirmDisabled = weightHundredths <= 0 || isSubmitting;

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="xs">
      <DialogTitle>{t("enter product weight")}</DialogTitle>
      <DialogContent>
        <Stack py={4} alignItems="center">
          <TextField
            fullWidth
            autoFocus
            type="text"
            inputMode="decimal"
            onKeyDown={(event) => handleKeyDown(event.key)}
            value={formatWeight(weightHundredths)}
            slotProps={{
              htmlInput: {
                "aria-label": t("enter product weight")
              },
              input: {
                sx: {
                  fontSize: 72,
                  fontWeight: "bold",
                  color: "primary.main",
                  bgcolor: "action.hover"
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ fontSize: 72, color: "text.primary" }}>
                      lbs
                    </Typography>
                  </InputAdornment>
                )
              }
            }}
          />
          <Stack width="100%" alignItems="center" py={4}>
            {NUMPAD_ROWS.map((row) => (
              <Stack key={row.join("-")} width="100%" alignItems="center">
                <NumpadRow>
                  {row.map((digit, i) => (
                    <NumpadKey
                      key={digit}
                      borderLeft={i !== 0}
                      label={digit}
                      onClick={() => handleDigitPress(Number(digit))}
                    />
                  ))}
                </NumpadRow>
              </Stack>
            ))}
            <NumpadRow>
              <Box sx={{ width: 80, height: 80 }} />
              <NumpadKey
                label="0"
                aria-label="0"
                borderLeft
                onClick={() => handleDigitPress(0)}
              />
              <IconButton
                aria-label={t("backspace")}
                onClick={handleBackspace}
                sx={{
                  width: 80,
                  height: 80,
                  borderLeft: `1px solid ${palette.divider}`,
                  borderRadius: 0
                }}
              >
                <Undo20Px fill={palette.text.primary} />
              </IconButton>
            </NumpadRow>
          </Stack>
          <Stack direction="row" gap={2} px={3}>
            <ProgressButton
              buttonSize="medium"
              sx={{ minWidth: "120px" }}
              emphasis="low"
              variant="contained"
              color="secondary"
              disabled={isSubmitting}
              onClick={handleDialogClose}
            >
              {t("cancel")}
            </ProgressButton>
            <ProgressButton
              buttonSize="medium"
              sx={{ minWidth: "120px" }}
              emphasis="high"
              variant="contained"
              color="primary"
              disabled={isConfirmDisabled}
              startIcon={<Checkmark20Px fill="white" />}
              onClick={handleConfirm}
            >
              {t("confirm")}
            </ProgressButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export function WeightEntryModalButton() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const binIsPresent = useAppSelector(selectBinIsPresent);

  return (
    <Chip
      aria-label={t("scan or enter weight")}
      onClick={() => dispatch(openWeightEntryModal())}
      label={t("scan or enter weight")}
      disabled={!binIsPresent}
      sx={{
        fontSize: "1.25rem",
        color: "common.white",
        bgcolor: "primary.main",
        height: "auto",
        py: 1,
        "&:hover": {
          bgcolor: "primary.dark"
        }
      }}
    />
  );
}
